import { PageHeader, LoadingState, DataTable, EmptyState } from '../components/AdminPageUI';
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Card, CardContent, Button, TextField, Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Divider, FormControlLabel, Checkbox, Alert } from '@mui/material';
import api from '../utils/Api';
import { toast } from 'react-toastify';

export default function TeacherSalaryPage() {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [staffDialog, setStaffDialog] = useState(false);
  const [staff, setStaff] = useState({ name: '', phone: '', jobTitle: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const saveInProgress = useRef(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => { 
    fetchSalaries(); 
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/payroll/settings');
      setSettings(res.data.settings);
    } catch (e) {
      // Ignore
    }
  }

  const fetchSalaries = async () => {
    try {
      const res = await api.get('/payroll/salaries');
      setSalaries(res.data.salaries);
    } catch (err) {
      toast.error('Failed to load salaries');
    } finally { setLoading(false); }
  };

  const handleEdit = (salary) => {
    setSaveError('');
    setSelectedSalary({
      ...salary,
      contactPhone: salary.contactPhone || salary.teacher?.phone || salary.teacher?.user?.phone || '',
      originalContactPhone: salary.contactPhone || salary.teacher?.phone || salary.teacher?.user?.phone || '',
      accountDetails: salary.accountDetails || {},
      basicSalaryEnabled: Number(salary.baseSalary) > 0,
      baseSalary: salary.baseSalary == null ? '' : String(salary.baseSalary),
      allowances: (salary.allowances || []).map(c => ({ ...c, amount: c.amount == null ? '' : String(c.amount) })),
      deductions: (salary.deductions || []).map(c => ({ ...c, amount: c.amount == null ? '' : String(c.amount) }))
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (saveInProgress.current) return;
    setSaveError('');
    const amounts = [
      ...(selectedSalary.basicSalaryEnabled ? [selectedSalary.baseSalary] : []),
      ...selectedSalary.allowances.map(c => c.amount),
      ...selectedSalary.deductions.map(c => c.amount)
    ];
    if (amounts.some(value => !Number.isFinite(Number(value || 0)) || Number(value || 0) < 0)) {
      setSaveError('Enter a valid amount for each salary component');
      return;
    }
    saveInProgress.current = true;
    setSaving(true);
    try {
      const payload = {
        teacherId: selectedSalary.teacher._id,
        employeeModel: selectedSalary.employeeModel || 'Teacher',
        ...(selectedSalary.contactPhone !== selectedSalary.originalContactPhone
          ? { contactPhone: selectedSalary.contactPhone.trim() } : {}),
        baseSalary: selectedSalary.basicSalaryEnabled ? Number(selectedSalary.baseSalary || 0) : 0,
        allowances: selectedSalary.allowances.map(c => ({ ...c, amount: Number(c.amount || 0) })),
        deductions: selectedSalary.deductions.map(c => ({ ...c, amount: Number(c.amount || 0) })),
        accountDetails: selectedSalary.accountDetails
      };
      let response;
      try {
        response = await api.post('/payroll/salaries', payload);
      } catch (error) {
        // Salary assignments replace the same employee's structure, so one retry is safe.
        const temporaryFailure = !error.response || [502, 503, 504].includes(error.response.status);
        if (!temporaryFailure) throw error;
        await new Promise(resolve => setTimeout(resolve, 700));
        response = await api.post('/payroll/salaries', payload);
      }
      // Use the confirmed save response instead of a second request that can fail independently.
      setSalaries(current => current.map(salary => String(salary.teacher?._id) === String(payload.teacherId)
        ? { ...salary, ...payload, ...response.data?.salary, teacher: salary.teacher }
        : salary));
      toast.success('Salary structure updated');
      setOpenDialog(false);
    } catch (err) {
      const message = err.response?.data?.message || 'Could not save the salary. Check your connection and try again.';
      setSaveError(message);
      toast.error(message);
    } finally { saveInProgress.current = false; setSaving(false); }
  };

  const hasComponent = (compName, type) => {
    if (compName.toLowerCase() === 'basic salary') {
       return selectedSalary.basicSalaryEnabled;
    }
    const list = type === 'earning' ? selectedSalary.allowances : selectedSalary.deductions;
    return !!list.find(c => c.name === compName);
  };

  const toggleComponent = (comp) => {
    if (comp.name.toLowerCase() === 'basic salary') {
      setSelectedSalary(previous => ({
        ...previous,
        basicSalaryEnabled: !previous.basicSalaryEnabled,
        baseSalary: previous.basicSalaryEnabled ? '' : (comp.amount ? String(comp.amount) : '')
      }));
      return;
    }

    const isEarning = comp.type === 'earning';
    const listKey = isEarning ? 'allowances' : 'deductions';
    const list = selectedSalary[listKey];
    
    if (hasComponent(comp.name, comp.type)) {
      setSelectedSalary({...selectedSalary, [listKey]: list.filter(c => c.name !== comp.name)});
    } else {
      setSelectedSalary({...selectedSalary, [listKey]: [...list, { name: comp.name, amount: comp.amount ? String(comp.amount) : '' }]});
    }
  };

  const updateComponentAmount = (compName, type, val) => {
    // Keep draft text intact while typing, including an empty value or trailing decimal point.
    if (!/^\d*(?:\.\d{0,2})?$/.test(val)) return;
    if (compName.toLowerCase() === 'basic salary') {
      setSelectedSalary({...selectedSalary, baseSalary: val});
      return;
    }

    const isEarning = type === 'earning';
    const listKey = isEarning ? 'allowances' : 'deductions';
    const list = selectedSalary[listKey];
    
    const updated = list.map(c => c.name === compName ? { ...c, amount: val } : c);
    setSelectedSalary({...selectedSalary, [listKey]: updated});
  };

  if (loading) return <LoadingState />;

  return (
    <Box p={3}>
      <PageHeader title="Staff Salary Assignments" description="Manage teacher and non-teaching staff salaries and SMS phone numbers." actions={<Button variant="contained" sx={{
        mb: 2
      }} onClick={() => setStaffDialog(true)}>Add Non-Teaching Staff</Button>} />
      
      
      <Dialog open={staffDialog} onClose={() => !saving && setStaffDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Non-Teaching Staff</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth required margin="normal" label="Full Name" value={staff.name} onChange={e => setStaff({ ...staff, name: e.target.value })} />
          <TextField fullWidth required margin="normal" label="SMS Phone Number" placeholder="0241234567" value={staff.phone} onChange={e => setStaff({ ...staff, phone: e.target.value })} />
          <TextField fullWidth margin="normal" label="Job Title" value={staff.jobTitle} onChange={e => setStaff({ ...staff, jobTitle: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setStaffDialog(false)}>Cancel</Button>
          <Button disabled={saving || !staff.name.trim() || !staff.phone.trim()} variant="contained" onClick={async () => {
            setSaving(true);
            try {
              const res = await api.post('/payroll/staff', staff);
              setStaffDialog(false);
              setStaff({ name: '', phone: '', jobTitle: '' });
              await fetchSalaries();
              handleEdit({ teacher: { ...res.data.staff, user: { name: res.data.staff.name, phone: res.data.staff.phone } }, employeeModel: 'PayrollStaff', baseSalary: 0 });
              toast.success('Staff added. Set their salary below.');
            } catch (err) { toast.error(err.response?.data?.message || 'Failed to add staff'); }
            finally { setSaving(false); }
          }}>Add Staff</Button>
        </DialogActions>
      </Dialog>
      <Card>
        <CardContent>
          <DataTable><Table>
            <TableHead>
              <TableRow>
                <TableCell>Staff Name</TableCell>
                <TableCell>Staff Type</TableCell>
                <TableCell>SMS Phone</TableCell>
                <TableCell>Base Salary</TableCell>
                <TableCell>Bank Account</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
{salaries.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

              {salaries.map((s) => (
                <TableRow key={s.teacher?._id}>
                  <TableCell>{s.teacher?.user?.name || 'Unknown'}</TableCell>
                  <TableCell>{s.employeeModel === 'PayrollStaff' ? (s.teacher.jobTitle || 'Non-Teaching') : 'Teacher'}</TableCell>
                  <TableCell>{s.contactPhone || s.teacher?.phone || s.teacher?.user?.phone || 'Not set'}</TableCell>
                  <TableCell>GHS {s.baseSalary || 0}</TableCell>
                  <TableCell>{s.accountDetails?.accountNumber || 'Not set'}</TableCell>
                  <TableCell>
                    <Button variant="outlined" size="small" onClick={() => handleEdit(s)}>Manage Salary</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></DataTable>
        </CardContent>
      </Card>

      {selectedSalary && (
        <Dialog open={openDialog} onClose={() => !saving && setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit Salary - {selectedSalary.teacher?.user?.name}</DialogTitle>
          <DialogContent>
            {saveError && <Alert severity="error" sx={{ mt: 1 }} role="alert">{saveError}</Alert>}
            <Grid container spacing={2} sx={{ mt: 1 }}>

              <Grid size={{ xs: 12 }}  >
                <TextField fullWidth label="Salary SMS Phone Number" helperText="Used for salary notifications. Ghana numbers, e.g. 0241234567." value={selectedSalary.contactPhone || ''} onChange={e => setSelectedSalary({ ...selectedSalary, contactPhone: e.target.value })} />
              </Grid>
              {/* EARNINGS */}
              <Grid size={{ xs: 12, md: 6 }}   >
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Earnings / Allowances</Typography>
                {settings?.components?.filter(c => c.type === 'earning').map((comp, idx) => {
                  const isActive = hasComponent(comp.name, comp.type);
                  let activeAmount = 0;
                  if (comp.name.toLowerCase() === 'basic salary') {
                     activeAmount = selectedSalary.baseSalary;
                  } else if (isActive) {
                     const activeComp = selectedSalary.allowances.find(c => c.name === comp.name);
                     activeAmount = activeComp ? activeComp.amount : 0;
                  }
                  return (
                    <Box key={idx} display="flex" alignItems="center" mb={2}>
                      <FormControlLabel
                        control={<Checkbox checked={isActive} onChange={() => toggleComponent(comp)} />}
                        label={comp.name}
                        sx={{ width: '50%' }}
                      />
                      {isActive && (
                        <TextField 
                          size="small" 
                          type="text"
                          inputProps={{ inputMode: 'decimal', maxLength: 15 }}
                          placeholder="0.00"
                          onFocus={(e) => e.target.select()}
                          label="Amount (GHS)"
                          value={activeAmount ?? ''}
                          onChange={(e) => updateComponentAmount(comp.name, comp.type, e.target.value)}
                          sx={{ width: '40%' }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Grid>

              {/* DEDUCTIONS */}
              <Grid size={{ xs: 12, md: 6 }}   >
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Deductions</Typography>
                {settings?.components?.filter(c => c.type === 'deduction').map((comp, idx) => {
                  const isActive = hasComponent(comp.name, comp.type);
                  const activeComp = isActive ? selectedSalary.deductions.find(c => c.name === comp.name) : null;
                  return (
                    <Box key={idx} display="flex" alignItems="center" mb={2}>
                      <FormControlLabel
                        control={<Checkbox checked={isActive} onChange={() => toggleComponent(comp)} />}
                        label={comp.name}
                        sx={{ width: '50%' }}
                      />
                      {isActive && (
                        <TextField 
                          size="small" 
                          type="text"
                          inputProps={{ inputMode: 'decimal', maxLength: 15 }}
                          placeholder="0.00"
                          onFocus={(e) => e.target.select()}
                          label="Amount (GHS)"
                          value={activeComp?.amount ?? ''}
                          onChange={(e) => updateComponentAmount(comp.name, comp.type, e.target.value)}
                          sx={{ width: '40%' }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Grid>

              <Grid size={{ xs: 12 }}  ><Divider sx={{ my: 2 }} /><Typography variant="h6">Bank Details</Typography></Grid>
              <Grid size={{ xs: 12, md: 4 }}   >
                <TextField label="Bank Name" fullWidth value={selectedSalary.accountDetails?.bankName || ''} onChange={(e) => setSelectedSalary({...selectedSalary, accountDetails: {...selectedSalary.accountDetails, bankName: e.target.value}})} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}   >
                <TextField label="Account Number" fullWidth value={selectedSalary.accountDetails?.accountNumber || ''} onChange={(e) => setSelectedSalary({...selectedSalary, accountDetails: {...selectedSalary.accountDetails, accountNumber: e.target.value}})} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}   >
                <TextField label="SSNIT Number" fullWidth value={selectedSalary.accountDetails?.ssnitNumber || ''} onChange={(e) => setSelectedSalary({...selectedSalary, accountDetails: {...selectedSalary.accountDetails, ssnitNumber: e.target.value}})} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button disabled={saving} onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save Changes'}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
